import { useEffect, useState } from "react";
import { GAMES, gameName, opisRangi, parseAnswerKey } from "@gierki/shared";
import {
  api,
  type BilansWiersz,
  type FaktWiersz,
  type PartiaSzczegoly,
  type PartiaWiersz,
  type ProfilFakt,
  type ProfilGracza,
  type Rekord,
  type ZdarzenieWiersz,
} from "../api.ts";
import { Ekran, Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";

type Zakladka = "bilans" | "rekordy" | "historia" | "gracze";

const ZAKLADKI: Array<[Zakladka, string]> = [
  ["bilans", "Bilans"],
  ["rekordy", "Rekordy"],
  ["historia", "Historia"],
  ["gracze", "Gracze"],
];

/**
 * Etykiety kodów faktów w jednym miejscu — dokłada się tu nowy kod, gdy
 * przybędzie gra albo statystyka, zamiast rozrzucać `if`-y po zakładkach.
 * `kategoria:<nazwa>` w Kościach nie ma stałej listy nazw kategorii, więc
 * dostaje osobną gałąź w `etykietaFaktu`.
 */
const ETYKIETY_FAKTOW: Record<string, string> = {
  najdluzszy_wyraz: "Najdłuższe słowo",
  najlepszy_ruch: "Najlepszy ruch",
  wymiany: "Wymiany liter",
  pasy: "Pasy",
  generaly: "Generały",
  bonus_gorny: "Bonus za górną część",
  najlepsza_runda: "Najlepsza runda",
  odpowiedzi_unikalne: "Unikalne odpowiedzi",
  odpowiedzi_odrzucone: "Odrzucone odpowiedzi",
  rycerze: "Rycerze",
  osady: "Osady",
  miasta: "Miasta",
  najdluzsza_droga: "Najdłuższa droga",
  najwieksza_armia: "Największa armia",
  tury: "Tury",
  wartosc_stada: "Wartość stada",
  lisy: "Napady lisa",
  wilki: "Napady wilka",
  psy_uratowaly: "Uratowane przez psa",
  wymiany_zwierzat: "Wymiany zwierząt",

  // Perudo
  kosci_koncowe: "Kości na koniec",
  dudo_trafione: "Trafione dudo",
  dudo_chybione: "Chybione dudo",
  calza_trafione: "Trafiona calza",
  calza_chybione: "Chybiona calza",
  zaklady: "Zakłady",
  zaklady_obronione: "Obronione zakłady",
  rundy_perudo: "Rundy",
  palifico_rundy: "Rundy palifico",

  // Literaki
  najdluzsze_slowo: "Najdłuższe słowo z zestawu",
  slowa_ulozone: "Ułożone słowa",
  slowa_unikalne: "Słowa tylko u jednej osoby",
  slowa_odrzucone: "Odrzucone słowa",

  // Wordle
  hasla_trafione: "Odgadnięte hasła",
  haslo_najmniej_prob: "Hasło w najmniejszej liczbie prób",
  haslo_pierwszy: "Odgadnięte jako pierwszy",
  pudla: "Spalone hasła",

  // Makao
  zagrane_karty: "Zagrane karty",
  dobrane_karty: "Dobrane karty",
  kary_dobrane: "Karty dobrane z kary",
  najwieksza_kara: "Największa kara",
  krole_bojowe: "Króle bojowe",
  walety: "Walety",
  asy: "Asy",
  czworki: "Czwórki",
  zapomniane_makao: "Zapomniane makao",
  zlapane_makao: "Złapane makao",
  karty_na_koncu: "Karty na koniec",
  kolejki_makao: "Kolejki",

  // Statki
  celnosc: "Celność",
  zatopione: "Zatopione statki",
  seria_trafien: "Najdłuższa seria trafień",
  ocalale: "Ocalałe statki",
  salwy_statki: "Salwy",
};

const PREFIKS_KATEGORII = "kategoria:";
/** Superfarmer liczy stado końcowe gatunkami — lista gatunków żyje po stronie gry. */
const PREFIKS_ZWIERZAT = "zwierze:";

const ZWIERZETA_LABEL: Record<string, string> = {
  krolik: "króliki",
  owca: "owce",
  swinia: "świnie",
  krowa: "krowy",
  kon: "konie",
  malyPies: "małe psy",
  duzyPies: "duże psy",
};

/**
 * Kategorie Kości o **stałej** punktacji.
 *
 * Dla nich najlepszy wynik nic nie mówi — full to zawsze 25 albo zero. Sensem
 * jest, jak często udało się je zaliczyć, więc profil pokazuje tam procent
 * zamiast liczby. Reszta kategorii to sumy oczek i tam procent byłby bez treści.
 *
 * Lista stoi po stronie ekranu, obok etykiet, tak jak reszta wiedzy o kodach
 * faktów — serwer wysyła liczby, nazywanie ich jest robotą klienta.
 */
const KATEGORIE_STALE = new Set(["full", "malyStrit", "duzyStrit", "general"]);

function stalaPunktacja(kod: string): boolean {
  return (
    kod.startsWith(PREFIKS_KATEGORII) && KATEGORIE_STALE.has(kod.slice(PREFIKS_KATEGORII.length))
  );
}

/**
 * Grupuje wiersze po grze, w kolejności katalogu gier.
 *
 * Wspólne dla rekordów i profilu, bo obie listy były tym samym grochem
 * z kapustą: „Kategoria: full" sąsiadowała z „Najdłuższą drogą" i nic nie
 * mówiło, że to dwie różne gry. Gra nieznana tej wersji aplikacji ląduje
 * na końcu zamiast wypaść z listy.
 */
function pogrupujPoGrach<T extends { gameType: string }>(
  wiersze: readonly T[],
): Array<{ gameType: string; wiersze: T[] }> {
  const kolejnosc = GAMES.map((gra) => gra.id);
  const grupy = new Map<string, T[]>();
  for (const wiersz of wiersze) {
    const grupa = grupy.get(wiersz.gameType);
    if (grupa) grupa.push(wiersz);
    else grupy.set(wiersz.gameType, [wiersz]);
  }
  return [...grupy.entries()]
    .map(([gameType, lista]) => ({ gameType, wiersze: lista }))
    .sort((a, b) => {
      const ia = kolejnosc.indexOf(a.gameType);
      const ib = kolejnosc.indexOf(b.gameType);
      return (ia === -1 ? GAMES.length : ia) - (ib === -1 ? GAMES.length : ib);
    });
}

/** Nagłówek sekcji gry — jeden styl dla rekordów i dla profilu. */
function NaglowekGry({ gameType }: { gameType: string }) {
  return (
    <h3 className="text-sm uppercase tracking-wide text-tekst-slaby pt-2">
      {gameName(gameType)}
    </h3>
  );
}

function etykietaFaktu(kod: string): string {
  if (kod.startsWith(PREFIKS_KATEGORII)) return `Kategoria: ${kod.slice(PREFIKS_KATEGORII.length)}`;
  if (kod.startsWith(PREFIKS_ZWIERZAT)) {
    const zwierze = kod.slice(PREFIKS_ZWIERZAT.length);
    return `Stado: ${ZWIERZETA_LABEL[zwierze] ?? zwierze}`;
  }
  return ETYKIETY_FAKTOW[kod] ?? kod;
}

function formatujDate(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatujCzas(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Etykiety typów zdarzeń dla osi zdarzeń partii — obok etykiet faktów, z tego
 * samego powodu: jedno miejsce zamiast `if`-ów rozrzuconych po ekranie.
 *
 * Lista pokrywa wszystko, co faktycznie pchają cztery pluginy do
 * `DomainEvent[]` (`server/src/games/*​/plugin.ts`), włącznie z dwoma typami
 * spoza wykazu w tym zadaniu (`word-challenged`, `round-started`) — inaczej
 * po polsku byłaby tylko część osi, a reszta raw `typ`em po angielsku.
 * Typ, który tu nie trafił (nowa gra, nowa funkcja), i tak nie wywali
 * ekranu: `opisZdarzenia`/render niżej mają dla nieznanego typu fallback
 * na sam `zdarzenie.typ`.
 */
const ETYKIETY_ZDARZEN: Record<string, string> = {
  "turn-started": "Nowa tura",
  rzut: "Rzut kośćmi",
  zapis: "Wpis do tabeli",
  ruch: "Ruch",
  wymiana: "Wymiana płytek",
  pas: "Pas",
  "word-challenged": "Sprzeciw wobec słowa",
  sprzeciw: "Sprzeciw",
  glos: "Głos",
  runda: "Koniec rundy",
  "round-started": "Nowa runda",
  "round-stopping": "STOP",
  "round-ended": "Czas rundy minął",
  osada: "Osada",
  droga: "Droga",
  zbudowano: "Budowa",
  zlodziej: "Złodziej",
  kradziez: "Kradzież",
  rycerz: "Rycerz",
  handel: "Handel",
  "karta-rozwoju": "Karta rozwoju",
  przekaz: "Przekaz surowców",
  odrzucanie: "Odrzucanie kart",
  koniec: "Koniec partii",
  "wymiana-zwierzat": "Wymiana zwierząt",
  "wymiana-odrzucona": "Odrzucona propozycja",
  propozycja: "Propozycja wymiany",
  superfarmer: "Komplet stada",

  /*
   * Zdarzenie wspólne dla platformy — patrz „Wspólne nazwy w pluginach gier"
   * w CLAUDE.md. Pięć gier miało na to cztery różne nazwy, zanim je zrównano.
   */
  odpadniecie: "Koniec dla gracza",

  // Perudo
  zaklad: "Zakład",
  dudo: "Dudo",
  calza: "Calza",
  odkrycie: "Odkrycie kubków",
  "strata-kosci": "Strata kości",
  palifico: "Palifico",

  // Literaki
  slowo: "Słowo",
  "slowo-odrzucone": "Słowo odrzucone",
  podsumowanie: "Podsumowanie rundy",

  // Wordle
  proba: "Próba",
  trafienie: "Trafienie",
  haslo: "Hasło rundy",

  // Makao
  zagranie: "Zagranie karty",
  dobranie: "Dobranie karty",
  "kara-nalozona": "Kara nałożona",
  "kara-przyjeta": "Kara przyjęta",
  zadanie: "Żądanie",
  "makao-zgloszone": "Makao!",
  lapy: "Łapy!",
  przetasowanie: "Przetasowanie stosu",

  // Statki
  flota: "Flota rozstawiona",
  salwa: "Salwa",
  zatopienie: "Zatopienie",
};

/** Polskie nazwy surowców Catanu — `welna`/`zboze` w danych to ASCII bez ogonków. */
const SUROWIEC_LABEL: Record<string, string> = {
  drewno: "drewno",
  welna: "wełna",
  zboze: "zboże",
  glina: "glina",
  ruda: "ruda",
};

/** Odmiana biernika do „zbudował(a) drogę/osadę/miasto". */
const BUDOWLA_BIERNIK: Record<string, string> = {
  droga: "drogę",
  osada: "osadę",
  miasto: "miasto",
};

function etykietaZdarzenia(typ: string): string {
  return ETYKIETY_ZDARZEN[typ] ?? typ;
}

function jakoObiekt(payload: unknown): Record<string, unknown> {
  return payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
}

function jakoTekst(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function jakoLiczbe(v: unknown): number | null {
  return typeof v === "number" ? v : null;
}

/** Id gracza z payloadu na nick — `nicki` to mapa zbudowana z wyników partii, jedynego miejsca, gdzie klient zna czyj to identyfikator. */
function nickZPayloadu(v: unknown, nicki: Record<string, string>): string {
  if (typeof v !== "string" && typeof v !== "number") return "";
  const id = String(v);
  return nicki[id] ?? id;
}

/**
 * Treść zdarzenia z jego payloadu, po polsku, z nickami zamiast surowych id.
 *
 * `recipients` nie trafia do dziennika (patrz `server/src/stats/repository.ts`
 * `zapiszZdarzenia`) — dlatego kilka typów (`turn-started`, `zlodziej`,
 * `przekaz`, `odrzucanie`, `round-ended`) nie niesie żadnej treści poza samym
 * faktem zajścia: serwer nigdy nie zapisał, kogo to dotyczyło. `null` tutaj
 * oznacza właśnie to, nie błąd — wiersz i tak pokazuje etykietę i czas.
 *
 * `rzut` ma dwa różne kształty payloadu w dwóch grach (Kości: `gracz`
 * i pięć kości; Catan: dwie kości i `suma`, bez gracza) — rozróżnione po
 * obecności pola `suma`, bo `gameType` nie jest tu przekazywany osobno.
 */
/** Ściany kostek Superfarmera w liczbie pojedynczej — do opisu rzutu. */
const SCIANA_LABEL: Record<string, string> = {
  krolik: "królik",
  owca: "owca",
  swinia: "świnia",
  krowa: "krowa",
  kon: "koń",
  malyPies: "mały pies",
  duzyPies: "duży pies",
  wilk: "wilk",
  lis: "lis",
};

/**
 * Tabela wymian Superfarmera do opisu zdarzenia.
 *
 * Kursy są w payloadzie samym identyfikatorem, bo dziennik ma trzymać dane,
 * nie zdania. Rozwinięcie identyfikatora w „6 królików → 1 owca" to ta sama
 * robota, co `ETYKIETY_FAKTOW` obok — nazywanie liczb jest zadaniem ekranu.
 */
const KURSY_LABEL: Record<string, [string, string]> = {
  "krolik-owca": ["6 królików", "1 owca"],
  "owca-swinia": ["2 owce", "1 świnia"],
  "swinia-krowa": ["3 świnie", "1 krowa"],
  "krowa-kon": ["2 krowy", "1 koń"],
  "owca-malyPies": ["1 owca", "1 mały pies"],
  "krowa-duzyPies": ["1 krowa", "1 duży pies"],
};

/** `{ krolik: 3, owca: 1 }` → „3 króliki, 1 owca". */
function opiszStado(wartosc: unknown): string | null {
  const wpisy = Object.entries(jakoObiekt(wartosc))
    .map(([zwierze, ile]) => [zwierze, jakoLiczbe(ile) ?? 0] as const)
    .filter(([, ile]) => ile > 0);
  if (wpisy.length === 0) return null;
  return wpisy.map(([zwierze, ile]) => `${ile} ${ZWIERZETA_LABEL[zwierze] ?? zwierze}`).join(", ");
}

function opisKursu(kod: unknown, razy: number, odwrotnie: boolean): string {
  const para = KURSY_LABEL[jakoTekst(kod) ?? ""];
  if (!para) return "?";
  const [lewa, prawa] = odwrotnie ? [para[1], para[0]] : para;
  return `${lewa} → ${prawa}${razy > 1 ? ` ×${razy}` : ""}`;
}

function opisZdarzenia(typ: string, payloadRaw: unknown, nicki: Record<string, string>): string | null {
  const p = jakoObiekt(payloadRaw);

  switch (typ) {
    case "rzut": {
      /*
       * Dwie gry emitują `rzut` i mają różne payloady: Kości oczka (`kosci`),
       * Superfarmer dwie ściany ze zwierzętami (`sciany`). Bez tej gałęzi cała
       * oś Superfarmera pokazywała „michal: ?".
       */
      const sciany = Array.isArray(p.sciany)
        ? p.sciany.filter((s): s is string => typeof s === "string")
        : null;
      if (sciany && sciany.length > 0) {
        const czesci = [
          `${nickZPayloadu(p.gracz, nicki)}: ${sciany.map((s) => SCIANA_LABEL[s] ?? s).join(" + ")}`,
        ];
        const przyrost = opiszStado(p.przyrost);
        if (przyrost) czesci.push(`+${przyrost}`);
        const strata = opiszStado(p.strata);
        if (strata) czesci.push(`traci ${strata}`);
        const ocalily = Array.isArray(p.ocalily)
          ? p.ocalily.filter((s): s is string => typeof s === "string")
          : [];
        if (ocalily.length > 0) {
          czesci.push(`broni ${ocalily.map((s) => SCIANA_LABEL[s] ?? s).join(", ")}`);
        }
        return czesci.join(" · ");
      }

      const kosci = Array.isArray(p.kosci) ? p.kosci.filter((k): k is number => typeof k === "number") : null;
      const suma = jakoLiczbe(p.suma);
      if (suma !== null) return kosci ? `${kosci.join(" + ")} = ${suma}` : `Suma ${suma}`;
      if (p.gracz !== undefined) {
        return `${nickZPayloadu(p.gracz, nicki)}: ${kosci && kosci.length > 0 ? kosci.join(", ") : "?"}`;
      }
      return null;
    }

    case "zapis": {
      const kategoria = jakoTekst(p.kategoria);
      const punkty = jakoLiczbe(p.punkty);
      return `${nickZPayloadu(p.gracz, nicki)} — ${kategoria ?? "?"}: ${punkty ?? "?"} pkt`;
    }

    case "ruch": {
      const slowa = Array.isArray(p.slowa) ? p.slowa.filter((s): s is string => typeof s === "string") : [];
      const punkty = jakoLiczbe(p.punkty);
      const bingo = p.bingo === true;
      return `${nickZPayloadu(p.gracz, nicki)}: ${slowa.join(", ") || "?"} (${punkty ?? "?"} pkt)${bingo ? " — bingo!" : ""}`;
    }

    case "wymiana": {
      const ile = jakoLiczbe(p.ile);
      return `${nickZPayloadu(p.gracz, nicki)} wymienił(a) ${ile ?? "?"} płytek`;
    }

    case "pas":
      return `${nickZPayloadu(p.gracz, nicki)} spasował(a)`;

    case "word-challenged": {
      const slowa = Array.isArray(p.words) ? p.words.filter((s): s is string => typeof s === "string") : [];
      return slowa.length > 0 ? `Zakwestionowano: ${slowa.join(", ")}` : null;
    }

    case "sprzeciw": {
      const klucz = jakoTekst(p.klucz);
      const rozbite = klucz ? parseAnswerKey(klucz) : null;
      const kogo = rozbite ? nickZPayloadu(rozbite.playerId, nicki) : null;
      return `${nickZPayloadu(p.kto, nicki)} kwestionuje odpowiedź${kogo ? ` gracza ${kogo}` : ""}${rozbite?.category ? ` (${rozbite.category})` : ""}`;
    }

    case "glos": {
      const klucz = jakoTekst(p.klucz);
      const rozbite = klucz ? parseAnswerKey(klucz) : null;
      const przyjmuje = p.przyjmuje === true;
      return `${nickZPayloadu(p.kto, nicki)}: ${przyjmuje ? "uznaje" : "odrzuca"}${rozbite?.category ? ` (${rozbite.category})` : ""}`;
    }

    case "runda": {
      const litera = jakoTekst(p.litera);
      const punkty = jakoObiekt(p.punkty);
      const czesci = Object.entries(punkty)
        .map(([gracz, wartosc]) => `${nickZPayloadu(gracz, nicki)} +${jakoLiczbe(wartosc) ?? 0}`)
        .join(", ");
      return `Litera „${litera ?? "?"}"${czesci ? ` — ${czesci}` : ""}`;
    }

    case "round-started": {
      const litera = jakoTekst(p.letter);
      if (litera) return `Litera „${litera}"`;
      // Perudo dokłada `palifico` obok wspólnego `roundNo` — bez tej gałęzi
      // runda o zmienionych regułach wyglądałaby w dzienniku jak każda inna.
      if (p.palifico === true) return "Runda palifico";
      return null;
    }

    /*
     * Wordle. Ranga jest tu tym samym, czym na ekranie wyników: po partii widać,
     * czy hasła były trudne dlatego, że gracze mieli słaby dzień, czy dlatego, że
     * próg popularności był ustawiony za luźno. Brak rangi (hasło spoza listy
     * częstości) pokazuje samo hasło — `opisRangi` zwraca wtedy `null`.
     */
    case "haslo": {
      const haslo = jakoTekst(p.haslo);
      if (!haslo) return null;
      const opis = opisRangi(jakoLiczbe(p.ranga));
      return opis === null ? haslo : `${haslo} — ${opis}`;
    }

    case "round-stopping":
      return `${nickZPayloadu(p.by, nicki)} nacisnął(-ęła) STOP`;

    case "osada":
      return `${nickZPayloadu(p.gracz, nicki)} stawia osadę`;

    case "droga":
      return `${nickZPayloadu(p.gracz, nicki)} stawia drogę`;

    case "zbudowano": {
      const co = jakoTekst(p.co);
      return `${nickZPayloadu(p.gracz, nicki)} buduje ${co ? (BUDOWLA_BIERNIK[co] ?? co) : "coś"}`;
    }

    case "kradziez":
      return `${nickZPayloadu(p.zlodziej, nicki)} kradnie kartę`;

    case "rycerz":
      return `${nickZPayloadu(p.gracz, nicki)} zagrywa rycerza`;

    case "handel": {
      const oddaje = jakoTekst(p.oddaje);
      const bierze = jakoTekst(p.bierze);
      return `${nickZPayloadu(p.gracz, nicki)}: ${oddaje ? (SUROWIEC_LABEL[oddaje] ?? oddaje) : "?"} → ${bierze ? (SUROWIEC_LABEL[bierze] ?? bierze) : "?"}`;
    }

    case "karta-rozwoju":
      return `${nickZPayloadu(p.gracz, nicki)} kupuje kartę rozwoju`;

    case "koniec": {
      const zwyciezca = nickZPayloadu(p.zwyciezca, nicki);
      if (zwyciezca) return `Zwycięzca: ${zwyciezca}`;
      /*
       * `zwyciezca: null` znaczy remis i jest częścią kontraktu tego zdarzenia,
       * a nie brakiem danych — dlatego rozróżniamy „klucz jest, wartość pusta"
       * od „klucza nie ma wcale". Bez tego remis w Statkach wyglądałby jak
       * zdarzenie, którego ekran nie umie opisać.
       */
      return "zwyciezca" in p ? "Remis" : null;
    }

    case "odpadniecie": {
      const miejsce = jakoLiczbe(p.miejsce);
      const kto = nickZPayloadu(p.gracz, nicki);
      return miejsce !== null ? `${kto} kończy na ${miejsce}. miejscu` : `${kto} odpada`;
    }

    case "wymiana-zwierzat": {
      const kurs = opisKursu(p.kurs, jakoLiczbe(p.razy) ?? 1, p.odwrotnie === true);
      const komu = jakoTekst(p.komu);
      const zKim = komu ? ` z ${nickZPayloadu(komu, nicki)}` : " ze stadem";
      return `${nickZPayloadu(p.gracz, nicki)}${zKim}: ${kurs}`;
    }

    case "propozycja": {
      const kurs = opisKursu(p.kurs, jakoLiczbe(p.razy) ?? 1, p.odwrotnie === true);
      return `${nickZPayloadu(p.od, nicki)} proponuje: ${kurs}`;
    }

    case "wymiana-odrzucona":
      return p.powod === "czas" ? "Propozycja wygasła" : "Propozycja odrzucona";

    case "superfarmer":
      return `${nickZPayloadu(p.gracz, nicki)} kompletuje stado`;

    /*
     * Perudo. Zakład to para „ile × oczko" i tak też się go czyta przy stole —
     * rozwijanie tego w zdanie („co najmniej sześć piątek") robi z dziennika
     * powieść, a szukamy w nim liczby.
     */
    case "zaklad":
      return `${nickZPayloadu(p.gracz, nicki)}: ${jakoLiczbe(p.ile) ?? "?"} × ${jakoLiczbe(p.oczko) ?? "?"}`;

    case "dudo":
    case "calza": {
      const zaklad = jakoObiekt(p.zaklad);
      const para = `${jakoLiczbe(zaklad.ile) ?? "?"} × ${jakoLiczbe(zaklad.oczko) ?? "?"}`;
      const wolanie = typ === "dudo" ? "kłamiesz" : "dokładnie tyle";
      const ocena = p.trafil === true ? "trafione" : "chybione";
      return `${nickZPayloadu(p.gracz, nicki)}: „${wolanie}” na ${para} — było ${jakoLiczbe(p.policzono) ?? "?"}, ${ocena}`;
    }

    case "odkrycie":
      return `Na stole było ${jakoLiczbe(p.policzono) ?? "?"}${p.palifico === true ? " (palifico)" : ""}`;

    case "strata-kosci": {
      const zostalo = jakoLiczbe(p.zostalo);
      const kto = nickZPayloadu(p.gracz, nicki);
      return zostalo !== null ? `${kto} traci kość, zostaje mu ${zostalo}` : `${kto} traci kość`;
    }

    case "palifico":
      return `${nickZPayloadu(p.gracz, nicki)} ma jedną kość — następna runda to palifico`;

    default:
      return null;
  }
}

/**
 * Statystyki rodzinne.
 *
 * Poza `store.ts` i poza WebSocketem: statystyki czyta się po partii, nie
 * w trakcie, a stan globalny to kolejna rzecz do unieważniania.
 */
export function Statystyki({ onWroc }: { onWroc: () => void }) {
  const [zakladka, setZakladka] = useState<Zakladka>("bilans");

  return (
    <Ekran
      tytul="Statystyki"
      akcja={
        <Przycisk wariant="cichy" onClick={onWroc}>
          Wróć
        </Przycisk>
      }
    >
      <div className="grid grid-cols-4 gap-1">
        {ZAKLADKI.map(([klucz, etykieta]) => (
          <button
            key={klucz}
            onClick={() => setZakladka(klucz)}
            aria-pressed={zakladka === klucz}
            className={[
              // 48 px — próg z `ui.tsx` dla celów dotykowych trafianych kciukiem
              // bez patrzenia. Szkielet z brief-u miał tu 40 px; cztery zakładki
              // mieszczą się na szerokości telefonu z zapasem na wyższy rząd.
              "min-h-12 rounded-xl border text-sm transition-colors",
              zakladka === klucz
                ? "bg-akcent border-akcent text-tlo"
                : "bg-panel-jasny border-obrys text-tekst-slaby",
            ].join(" ")}
          >
            {etykieta}
          </button>
        ))}
      </div>

      {zakladka === "bilans" ? <ZakladkaBilans /> : null}
      {zakladka === "rekordy" ? <ZakladkaRekordy /> : null}
      {zakladka === "historia" ? <ZakladkaHistoria /> : null}
      {zakladka === "gracze" ? <ZakladkaGracze /> : null}
    </Ekran>
  );
}

/**
 * Bilans: pogrupowany po graczu, wiersz na każdą grę, w kolejności z serwera
 * (nick, potem gra).
 *
 * Fetch własny, nie dzielony z `ZakladkaRekordy` mimo wspólnego endpointu
 * `/api/stats` — oba komponenty żyją tylko, gdy ich zakładka jest aktywna
 * (montują się i odmontowują z przełącznikiem w `Statystyki`), więc dodatkowe
 * zapytanie przy przełączeniu jest tanie, a w zamian błąd i ponowna próba
 * (przełącz zakładkę i wróć) działają tak samo jak w Historii i Graczach,
 * bez osobnej maszynerii do retry.
 */
function ZakladkaBilans() {
  const [bilans, setBilans] = useState<BilansWiersz[] | null | "blad">(null);

  useEffect(() => {
    if (bilans !== null) return;
    api
      .stats()
      .then((dane) => setBilans(dane.bilans))
      .catch(() => setBilans("blad"));
  }, [bilans]);

  if (bilans === null) return null;
  if (bilans === "blad") {
    return <Komunikat rodzaj="blad">Nie udało się wczytać bilansu.</Komunikat>;
  }
  if (bilans.length === 0) {
    return <Komunikat>Żadna partia nie została jeszcze dograna do końca.</Komunikat>;
  }

  const poGraczu = new Map<string, { nick: string; wiersze: BilansWiersz[] }>();
  for (const wiersz of bilans) {
    const wpis = poGraczu.get(wiersz.userId);
    if (wpis) wpis.wiersze.push(wiersz);
    else poGraczu.set(wiersz.userId, { nick: wiersz.nick, wiersze: [wiersz] });
  }

  return (
    <>
      {[...poGraczu.entries()].map(([userId, { nick, wiersze }]) => (
        <Karta key={userId}>
          <div className="font-semibold">{nick}</div>
          <div className="mt-2 space-y-1.5">
            {wiersze.map((wiersz) => (
              <div key={wiersz.gameType} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-tekst-slaby">{gameName(wiersz.gameType)}</span>
                <span className="tabular">
                  {wiersz.wygrane}/{wiersz.rozegrane} zwycięstw · najlepszy {wiersz.najlepszy}
                </span>
              </div>
            ))}
          </div>
        </Karta>
      ))}
    </>
  );
}

/**
 * Rekordy wspólne: jedna karta na kod faktu. Kod, dla którego nikt jeszcze
 * nie ustawił wartości, po prostu nie ma wiersza w odpowiedzi — nie
 * renderuje się wcale. Fetch własny — patrz komentarz przy `ZakladkaBilans`.
 */
function ZakladkaRekordy() {
  const [rekordy, setRekordy] = useState<Rekord[] | null | "blad">(null);

  useEffect(() => {
    if (rekordy !== null) return;
    api
      .stats()
      .then((dane) => setRekordy(dane.rekordy))
      .catch(() => setRekordy("blad"));
  }, [rekordy]);

  if (rekordy === null) return null;
  if (rekordy === "blad") {
    return <Komunikat rodzaj="blad">Nie udało się wczytać rekordów.</Komunikat>;
  }
  if (rekordy.length === 0) {
    return <Komunikat>Żadna partia nie została jeszcze dograna do końca.</Komunikat>;
  }

  return (
    <>
      {pogrupujPoGrach(rekordy).map((grupa) => (
        <div key={grupa.gameType} className="space-y-3">
          <NaglowekGry gameType={grupa.gameType} />
          {grupa.wiersze.map((rekord) => (
            <KartaRekordu key={rekord.kod} rekord={rekord} />
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Rekord z rozwijaną czołówką.
 *
 * Przyciskiem jest sam nagłówek, nie cała karta — tak samo jak w historii
 * i na liście graczy. Czołówka doładowuje się przy pierwszym otwarciu i zostaje
 * w stanie karty: zamknięcie i ponowne otwarcie nie odpytuje serwera drugi raz.
 */
function KartaRekordu({ rekord }: { rekord: Rekord }) {
  const [rozwiniety, setRozwiniety] = useState(false);
  const [top, setTop] = useState<Rekord[] | null | "blad">(null);

  useEffect(() => {
    if (!rozwiniety || top !== null) return;
    let anulowano = false;
    api
      .statsRecord(rekord.kod)
      .then((dane) => {
        if (!anulowano) setTop(dane.top);
      })
      .catch(() => {
        if (!anulowano) setTop("blad");
      });
    return () => {
      anulowano = true;
    };
  }, [rozwiniety, top, rekord.kod]);

  return (
    <Karta>
      <button
        type="button"
        aria-expanded={rozwiniety}
        onClick={() => setRozwiniety((czy) => !czy)}
        className="w-full text-left"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm text-tekst-slaby">{etykietaFaktu(rekord.kod)}</div>
          <div className="tabular font-semibold text-lg">{rekord.liczba}</div>
        </div>
        {rekord.tekst ? <div className="mt-1">{rekord.tekst}</div> : null}
        {rekord.nick ? (
          <div className="mt-1 text-sm text-tekst-slaby">
            {rekord.nick} · {formatujDate(rekord.finishedAt)}
          </div>
        ) : null}
      </button>

      {rozwiniety ? <CzolowkaRekordu top={top} /> : null}
    </Karta>
  );
}

function CzolowkaRekordu({ top }: { top: Rekord[] | null | "blad" }) {
  if (top === null) return <div className="mt-3 text-sm text-tekst-slaby">Wczytuję…</div>;
  if (top === "blad") {
    return <div className="mt-3 text-sm text-tekst-slaby">Nie udało się wczytać czołówki.</div>;
  }

  return (
    <div className="mt-3 pt-3 border-t border-obrys space-y-1">
      {top.map((wpis, miejsce) => (
        <div
          key={`${wpis.gameId}:${wpis.userId ?? miejsce}`}
          className="flex items-center justify-between gap-2 text-sm"
        >
          <span className="w-5 text-tekst-slaby tabular">{miejsce + 1}.</span>
          <span className="flex-1 truncate">
            {wpis.nick ?? "—"}
            {wpis.tekst ? <span className="text-tekst-slaby"> · {wpis.tekst}</span> : null}
          </span>
          <span className="text-tekst-slaby">{formatujDate(wpis.finishedAt)}</span>
          <span className="tabular w-10 text-right">{wpis.liczba}</span>
        </div>
      ))}
    </div>
  );
}

/** Wyniki jednej partii — dzielone przez kartę historii i przez rozwinięte szczegóły partii. */
function WynikiPartii({ wyniki }: { wyniki: PartiaWiersz["wyniki"] }) {
  return (
    <div className="mt-2 space-y-1">
      {wyniki.map((wynik) => (
        <div key={wynik.userId} className="flex items-center justify-between gap-2 text-sm">
          <span className={wynik.pozycja === 1 ? "font-semibold" : "text-tekst-slaby"}>
            {wynik.pozycja}. {wynik.nick}
          </span>
          <span className="tabular">{wynik.score}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Historia: strony po `limit` partii, kursor `before` to `gameId` ostatniej
 * partii poprzedniej strony.
 *
 * Błąd pierwszego wczytania i błąd „Pokaż więcej" to dwa osobne stany:
 * pierwszy zastępuje listę komunikatem, drugi zostawia już wczytane partie
 * na miejscu i tylko odmawia doładowania — zniknięcie przycisku wyglądałoby
 * jak koniec listy, a to nieprawda.
 */
function ZakladkaHistoria() {
  const [partie, setPartie] = useState<PartiaWiersz[] | null | "blad">(null);
  const [maWiecej, setMaWiecej] = useState(false);
  const [wczytujeWiecej, setWczytujeWiecej] = useState(false);
  const [bladWiecej, setBladWiecej] = useState(false);
  const [rozwinieta, setRozwinieta] = useState<string | null>(null);
  const LIMIT = 30;

  useEffect(() => {
    if (partie !== null) return;
    api
      .statsGames(LIMIT)
      .then((wynik) => {
        setPartie(wynik);
        setMaWiecej(wynik.length === LIMIT);
      })
      .catch(() => setPartie("blad"));
  }, [partie]);

  const wczytajWiecej = async () => {
    if (!partie || partie === "blad" || partie.length === 0) return;
    setWczytujeWiecej(true);
    setBladWiecej(false);
    try {
      const ostatnia = partie[partie.length - 1]!;
      const kolejne = await api.statsGames(LIMIT, ostatnia.gameId);
      setPartie([...partie, ...kolejne]);
      setMaWiecej(kolejne.length === LIMIT);
    } catch {
      // Lista zostaje jak była — tylko doładowanie się nie udało, nie koniec historii.
      setBladWiecej(true);
    } finally {
      setWczytujeWiecej(false);
    }
  };

  if (partie === null) return null;
  if (partie === "blad") {
    return <Komunikat rodzaj="blad">Nie udało się wczytać historii partii.</Komunikat>;
  }
  if (partie.length === 0) {
    return <Komunikat>Żadna partia nie została jeszcze dograna do końca.</Komunikat>;
  }

  return (
    <>
      {partie.map((partia) => (
        <Karta key={partia.gameId}>
          {/*
           * Tylko nagłówek jest przyciskiem — `SzczegolyPartii` niżej ma własny
           * scroll (oś zdarzeń), więc gdyby cała karta była `<button>`, każde
           * dotknięcie osi podczas przewijania zwijałoby właśnie otwarte
           * szczegóły. Zagnieżdżanie elementu interaktywnego w przycisku było
           * też nieprawidłowym HTML-em.
           */}
          <button
            type="button"
            onClick={() => setRozwinieta(rozwinieta === partia.gameId ? null : partia.gameId)}
            className="block w-full text-left"
            aria-expanded={rozwinieta === partia.gameId}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold">{gameName(partia.gameType)}</div>
              <div className="text-sm text-tekst-slaby">{formatujDate(partia.finishedAt)}</div>
            </div>
            <WynikiPartii wyniki={partia.wyniki} />
          </button>
          {rozwinieta === partia.gameId ? <SzczegolyPartii gameId={partia.gameId} /> : null}
        </Karta>
      ))}

      {bladWiecej ? <Komunikat rodzaj="blad">Nie udało się wczytać kolejnych partii.</Komunikat> : null}

      {maWiecej ? (
        <Przycisk wariant="zwykly" pelna onClick={() => void wczytajWiecej()} disabled={wczytujeWiecej}>
          {wczytujeWiecej ? "Wczytuję…" : "Pokaż więcej"}
        </Przycisk>
      ) : null}
    </>
  );
}

/**
 * Fakty i oś zdarzeń jednej partii — po rozwinięciu karty w historii.
 *
 * Oś idzie od najstarszego do najnowszego zdarzenia, tak jak partię ktoś by
 * opowiadał na głos — i tak samo `zdarzeniaDlaGry` w
 * `server/src/stats/repository.ts` już je zwraca (`ORDER BY id`, rosnąco),
 * więc tu nie trzeba niczego odwracać.
 */
function SzczegolyPartii({ gameId }: { gameId: string }) {
  const [szczegoly, setSzczegoly] = useState<PartiaSzczegoly | null | "blad">(null);

  useEffect(() => {
    let anulowano = false;
    api
      .statsGame(gameId)
      .then((wynik) => {
        if (!anulowano) setSzczegoly(wynik);
      })
      .catch(() => {
        if (!anulowano) setSzczegoly("blad");
      });
    return () => {
      anulowano = true;
    };
  }, [gameId]);

  if (szczegoly === null) return null;
  if (szczegoly === "blad") {
    return <div className="mt-2 text-sm text-tekst-slaby">Nie udało się wczytać szczegółów tej partii.</div>;
  }
  if (szczegoly.fakty.length === 0 && szczegoly.zdarzenia.length === 0) return null;

  // Do zamiany id gracza na nick w opisach zdarzeń — partia zna tylko tych
  // graczy, którzy w niej grali, więc mapa jest lokalna dla tych szczegółów.
  const nicki = Object.fromEntries(szczegoly.wyniki.map((wynik) => [wynik.userId, wynik.nick]));

  return (
    <div className="mt-3 pt-3 border-t border-obrys space-y-3">
      {szczegoly.fakty.length > 0 ? (
        <div className="space-y-1.5">
          {szczegoly.fakty.map((fakt: FaktWiersz, indeks: number) => (
            <div key={`${fakt.kod}-${indeks}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-tekst-slaby">
                {etykietaFaktu(fakt.kod)}
                {fakt.nick ? ` · ${fakt.nick}` : ""}
              </span>
              <span className="tabular">{fakt.tekst ?? fakt.liczba}</span>
            </div>
          ))}
        </div>
      ) : null}

      {szczegoly.zdarzenia.length > 0 ? (
        <div>
          <div className="text-sm text-tekst-slaby mb-1.5">Oś zdarzeń</div>
          {/* Własny scroll pionowy i poziomy — dziennik pełnej gry Kości to
              dziesiątki wierszy, a niektóre opisy (ruchy Scrabble) są długie. */}
          <div className="max-h-64 overflow-y-auto overflow-x-auto space-y-1">
            {szczegoly.zdarzenia.map((zdarzenie: ZdarzenieWiersz) => {
              const opis = opisZdarzenia(zdarzenie.typ, zdarzenie.payload, nicki);
              return (
                <div key={zdarzenie.id} className="text-sm whitespace-nowrap">
                  <span className="text-tekst-slaby tabular">{formatujCzas(zdarzenie.at)}</span>{" "}
                  <span className="font-medium">{etykietaZdarzenia(zdarzenie.typ)}</span>
                  {opis ? <span className="text-tekst-slaby"> — {opis}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Gracze: kafelki z listy kont, rozwijane w profil pod klikniętym kafelkiem. */
function ZakladkaGracze() {
  const [gracze, setGracze] = useState<Array<{ id: string; nick: string }> | null | "blad">(null);
  const [wybrany, setWybrany] = useState<string | null>(null);

  useEffect(() => {
    if (gracze !== null) return;
    api
      .users()
      .then(({ users }) => setGracze(users))
      .catch(() => setGracze("blad"));
  }, [gracze]);

  if (gracze === null) return null;
  if (gracze === "blad") {
    return <Komunikat rodzaj="blad">Nie udało się wczytać listy graczy.</Komunikat>;
  }
  if (gracze.length === 0) {
    return <Komunikat>Brak kont do pokazania.</Komunikat>;
  }

  return (
    <>
      {gracze.map((gracz) => (
        <Karta key={gracz.id}>
          {/* Ten sam powód co w `ZakladkaHistoria`: profil ma listę pod spodem,
              więc przyciskiem jest tylko nagłówek, nie cała karta. */}
          <button
            type="button"
            onClick={() => setWybrany(wybrany === gracz.id ? null : gracz.id)}
            className="block w-full text-left font-semibold"
            aria-expanded={wybrany === gracz.id}
          >
            {gracz.nick}
          </button>
          {wybrany === gracz.id ? <ProfilGraczaSzczegoly userId={gracz.id} /> : null}
        </Karta>
      ))}
    </>
  );
}

/**
 * Wartość faktu w profilu.
 *
 * Przy kategoriach Kości o stałej punktacji zamiast liczby idzie skuteczność:
 * „full 25" powtórzone przy każdym graczu nie niesie nic, bo full jest zawsze
 * za 25 — pytanie brzmi, jak często się go zamyka.
 */
function WartoscFaktu({ fakt }: { fakt: ProfilFakt }) {
  if (stalaPunktacja(fakt.kod) && fakt.partii > 0) {
    const procent = Math.round((fakt.zaliczone / fakt.partii) * 100);
    return (
      <span className="tabular">
        {fakt.zaliczone}/{fakt.partii} · {procent}%
      </span>
    );
  }
  /*
   * Fakt bywa parą: „najlepsza runda" to punkty **i** litera, „najlepszy ruch"
   * to punkty i słowa. Samo `tekst ?? liczba` pokazywało literę „E" w miejscu,
   * gdzie miało stać 120 punktów — czyli gubiło to, co w rekordzie jest istotne.
   */
  if (fakt.tekst !== null && fakt.tekst !== "") {
    return (
      <span className="tabular">
        {fakt.liczba} <span className="text-tekst-slaby">· {fakt.tekst}</span>
      </span>
    );
  }
  return <span className="tabular">{fakt.liczba}</span>;
}

function ProfilGraczaSzczegoly({ userId }: { userId: string }) {
  const [profil, setProfil] = useState<ProfilGracza | null | "blad">(null);

  useEffect(() => {
    let anulowano = false;
    api
      .statsPlayer(userId)
      .then((wynik) => {
        if (!anulowano) setProfil(wynik);
      })
      .catch(() => {
        if (!anulowano) setProfil("blad");
      });
    return () => {
      anulowano = true;
    };
  }, [userId]);

  if (profil === null) return null;
  if (profil === "blad") {
    return <div className="mt-2 text-sm text-tekst-slaby">Nie udało się wczytać profilu.</div>;
  }

  if (profil.rozegrane === 0) {
    return <div className="mt-2 text-sm text-tekst-slaby">Jeszcze żadnej dogranej partii.</div>;
  }

  return (
    <div className="mt-3 pt-3 border-t border-obrys space-y-3">
      <div className="flex items-center gap-2">
        <Odznaka aktywna>
          {profil.wygrane}/{profil.rozegrane} zwycięstw
        </Odznaka>
      </div>

      {profil.poGrach.length > 0 ? (
        <div className="space-y-1.5">
          {profil.poGrach.map((wpis) => (
            <div key={wpis.gameType} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-tekst-slaby">{gameName(wpis.gameType)}</span>
              <span className="tabular">
                {wpis.wygrane}/{wpis.rozegrane} · najlepszy {wpis.najlepszy}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {profil.fakty.length > 0
        ? pogrupujPoGrach(profil.fakty).map((grupa) => (
            <div key={grupa.gameType} className="space-y-1.5">
              <NaglowekGry gameType={grupa.gameType} />
              {grupa.wiersze.map((fakt: ProfilFakt) => (
                <div key={fakt.kod} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-tekst-slaby">{etykietaFaktu(fakt.kod)}</span>
                  <WartoscFaktu fakt={fakt} />
                </div>
              ))}
            </div>
          ))
        : null}
    </div>
  );
}
